import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { sendForgetPasswordEmail } from '../../redux/actions/forgetPasswordAction';
import { resetForgetPasswordState } from '../../redux/slices/forgetPasswordSlice';
import { useDarkMode } from '../../App';
import { motion } from 'framer-motion';
import { FiMail, FiArrowLeft, FiSmartphone, FiKey, FiGlobe } from 'react-icons/fi';
import '../AuthShared.css';

const LanguageSwitcher = () => {
    const { i18n } = useTranslation('global');
    const isAR = i18n.language === 'ar';

    const toggleLanguage = () => {
        const newLang = isAR ? 'en' : 'ar';
        i18n.changeLanguage(newLang);
        localStorage.setItem('lang', newLang);
    };

    return (
        <motion.button
            className="auth-lang-switcher"
            onClick={toggleLanguage}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            <FiGlobe />
            <span>{isAR ? 'English' : 'العربية'}</span>
        </motion.button>
    );
};

const ForgetPassword = () => {
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const forgetPasswordState = useSelector((state) => state.forgetPassword);
  const { isLoading, success, error, data } = forgetPasswordState || {};

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('forgetPassword.title', 'Forget Password');
  }, [t]);

  useEffect(() => {
    if (success) {
      toast.success(data?.message || t('forgetPassword.successMessage', 'Reset code sent to your email!'));
      dispatch(resetForgetPasswordState());
      setTimeout(() => navigate('/otp-forget', { state: { email } }), 1000);
    }
  }, [success, data, t, navigate, dispatch, email]);

  useEffect(() => {
    if (error) {
      toast(error);
      dispatch(resetForgetPasswordState());
    }
  }, [error, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetForgetPasswordState());
    };
  }, [dispatch]);

  const validateEmail = () => {
    if (!email) {
      toast(t('forgetPassword.emailRequired', 'Email is required'));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast(t('forgetPassword.emailInvalid', 'Please enter a valid email'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (validateEmail()) {
      dispatch(sendForgetPasswordEmail({ email, lang: i18n.language }));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`auth-page ${isDarkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-bg-overlay"></div>
      <div className="auth-blobs">
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
      </div>

      <LanguageSwitcher />

      <motion.div 
        className="auth-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="auth-card" variants={itemVariants}>
          <div className="auth-brand">
            <motion.div className="auth-brand-circle" whileHover={{ scale: 1.05, rotate: 5 }}>
              <FiKey style={{ fontSize: '24px', color: '#fff' }} />
            </motion.div>
            <h1>{t('forgetPassword.brandTitle', 'Security Recovery')}</h1>
          </div>

          <div className="auth-header">
            <h2>{t('forgetPassword.title', 'Forgot Password')}</h2>
            <p>{t('forgetPassword.subtitle', 'Enter your email to receive a secure reset code')}</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label htmlFor="email"><FiMail /> {t('forgetPassword.email', 'Email Address')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.com"
                className={submitted && !email ? 'error' : ''}
              />
            </div>

            <motion.button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? <span className="auth-spinner"></span> : t('forgetPassword.sendCode', 'Send Reset Code')}
            </motion.button>

            <div className="auth-footer">
              <p>
                {t('forgetPassword.rememberPassword', 'Remember password?')}{' '}
                <Link to="/login" className="auth-link">{t('forgetPassword.backToLogin', 'Login')}</Link>
              </p>
              <button type="button" className="auth-back-btn" onClick={() => navigate('/login')}>
                <FiArrowLeft style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                {t('forgetPassword.backToLogin', 'Back')}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgetPassword;

